import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Radio,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { TemplateEditor, TemplatePreview } from '@gba/questionnaire-antd';
import type { QuestionnaireSchema } from '@gba/questionnaire-antd';
import { validateTemplateJson } from '@gba/questionnaire-core/validate';
import { BlockSkeleton, FormSkeleton } from '@/ui/components';
import { useCustomers } from '@/api/Customers';
import {
  useCreateTemplate,
  usePublishTemplate,
  useTemplateForEdit,
  useUpdateTemplate,
} from '@/api/QuestionnaireTemplates';
import type {
  FieldError,
  TemplateEditDetail,
} from '@/api/QuestionnaireTemplates';

const STARTER: QuestionnaireSchema = {
  type: 'object',
  title: 'New Template',
  required: [],
  ui: { sections: [{ id: 'general', title: 'General', order: 1 }] },
  properties: {},
};

const SCOPE_OPERANDS = [
  { name: 'scope.engineType', label: 'Engine Type' },
  { name: 'scope.customerId', label: 'Customer Id' },
];

interface LogicRow {
  key: string;
  question: string;
  ruleType: string;
  summary: string;
}

interface TemplateEditorPanelProps {
  templateId?: number;
  onDone: () => void;
  onCancel: () => void;
}

export const TemplateEditorPanel: FC<TemplateEditorPanelProps> = ({
  templateId,
  onDone,
  onCancel,
}) => {
  const isEdit = templateId != null;
  const { data: customers = [] } = useCustomers();
  const {
    data: detail,
    isFetching,
    isError,
    error,
  } = useTemplateForEdit(templateId);
  const { mutateAsync: create } = useCreateTemplate();
  const { mutateAsync: update } = useUpdateTemplate();
  const { mutateAsync: publish } = usePublishTemplate();
  const [form] = Form.useForm();
  const [action, setAction] = useState<'draft' | 'publish'>();

  const [id, setId] = useState<number | undefined>(templateId);
  const [version, setVersion] = useState<number>();
  const [scope, setScope] = useState<'global' | 'customer'>('global');
  const [customerId, setCustomerId] = useState<number>();
  const [definition, setDefinition] = useState<QuestionnaireSchema>(STARTER);
  const [summary, setSummary] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  const [tab, setTab] = useState('build');
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);

  const jsonText = useMemo(
    () => JSON.stringify(definition, null, 2),
    [definition],
  );
  const jsonResult = useMemo(() => validateTemplateJson(jsonText), [jsonText]);

  const previewScope = useMemo(() => ({}), []);

  useEffect(() => {
    if (!detail) return;
    setId(detail.id);
    setVersion(detail.version);
    setScope(detail.customerId == null ? 'global' : 'customer');
    setCustomerId(detail.customerId);
    setDefinition(detail.definition);
    form.setFieldsValue({
      templateKey: detail.templateKey,
      title: detail.title,
    });
  }, [detail, form]);

  useEffect(() => {
    if (!isEdit || !isError) return;
    const res = error as { message?: any } | undefined;
    message.error(res?.message?.Message ?? 'Failed to load template.');
    onCancel();
  }, [isEdit, isError, error, onCancel]);

  const applyError = (res: { code: number; message?: any }) => {
    if (res.code === 400) {
      setSummary(res.message?.Message);
      setFieldErrors(res.message?.Data ?? []);
      return;
    }
    setSummary(undefined);
    setFieldErrors([]);
    message.error(res.message?.Message ?? 'Save failed.');
    if (res.code === 404) {
      onCancel();
    }
  };

  const onCopyJson = () => {
    void navigator.clipboard?.writeText(jsonText);
    message.success('Copied JSON.');
  };

  const persist = async (): Promise<TemplateEditDetail | null> => {
    let values: { templateKey?: string; title: string };
    try {
      values = await form.validateFields();
    } catch {
      return null;
    }
    setSummary(undefined);
    setFieldErrors([]);
    setSchemaErrors([]);
    if (!jsonResult.valid) {
      setSummary('Template schema is invalid.');
      setSchemaErrors(jsonResult.errors);
      return null;
    }
    const res = isEdit
      ? await update({ id: id!, body: { title: values.title, definition } })
      : await create({
          templateKey: values.templateKey!,
          customerId: scope === 'customer' ? customerId : undefined,
          title: values.title,
          definition,
        });
    if (!res.success) {
      applyError(res);
      return null;
    }
    setId(res.data.id);
    setVersion(res.data.version);
    return res.data;
  };

  const onSaveDraft = async () => {
    setAction('draft');
    try {
      const saved = await persist();
      if (!saved) return;
      message.success('Template saved.');
      onDone();
    } finally {
      setAction(undefined);
    }
  };

  const onPublish = async () => {
    setAction('publish');
    try {
      const saved = await persist();
      if (!saved) return;
      if (saved.status === 'Draft') {
        const res = await publish(saved.id);
        if (!res.success) {
          applyError(res);
          return;
        }
      }
      message.success('Template published.');
      onDone();
    } finally {
      setAction(undefined);
    }
  };

  const logicRows = useMemo<LogicRow[]>(() => {
    const rows: LogicRow[] = [];
    Object.entries(definition.properties ?? {}).forEach(([key, field]) => {
      if (field.visibleWhen !== undefined) {
        rows.push({
          key: `${key}-visible`,
          question: field.title || key,
          ruleType: 'Show when',
          summary: JSON.stringify(field.visibleWhen),
        });
      }
      if (field.requiredWhen !== undefined) {
        rows.push({
          key: `${key}-required`,
          question: field.title || key,
          ruleType: 'Required when',
          summary: JSON.stringify(field.requiredWhen),
        });
      }
    });
    return rows;
  }, [definition]);

  if (isEdit && (isFetching || isError)) {
    return (
      <Card
        title={<Skeleton.Input active style={{ width: 220 }} />}
        extra={<Skeleton.Button active />}
        styles={{ body: { padding: 16 } }}
      >
        <FormSkeleton fields={3} />
        <BlockSkeleton height={320} />
      </Card>
    );
  }

  const tabItems = [
    {
      key: 'build',
      label: 'Build',
      children: (
        <TemplateEditor
          value={definition}
          onChange={setDefinition}
          scopeOperands={SCOPE_OPERANDS}
        />
      ),
    },
    {
      key: 'preview',
      label: 'Preview',
      children: <TemplatePreview template={definition} scope={previewScope} />,
    },
    {
      key: 'logic',
      label: 'Logic',
      children: (
        <Table<LogicRow>
          size="small"
          rowKey="key"
          pagination={false}
          locale={{ emptyText: 'No logical rules yet' }}
          dataSource={logicRows}
          columns={[
            { title: 'Question', dataIndex: 'question', key: 'question' },
            { title: 'Rule', dataIndex: 'ruleType', key: 'ruleType' },
            { title: 'Condition', dataIndex: 'summary', key: 'summary' },
          ]}
        />
      ),
    },
    {
      key: 'json',
      label: 'JSON',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            {jsonResult.valid ? (
              <Tag color="success">Valid schema</Tag>
            ) : (
              <Tag color="error">Invalid</Tag>
            )}
            <Button size="small" onClick={onCopyJson}>
              Copy
            </Button>
            <Typography.Text type="secondary">
              Read-only. Edit questions in the Build tab.
            </Typography.Text>
          </Space>
          {!jsonResult.valid && jsonResult.errors.length ? (
            <Alert
              type="error"
              message="Invalid schema"
              description={
                <ul>
                  {jsonResult.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
          <Input.TextArea
            aria-label="Template JSON"
            value={jsonText}
            readOnly
            autoSize={{ minRows: 16 }}
            style={{ fontFamily: 'monospace' }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          {isEdit ? `Edit Template (v${version ?? ''})` : 'New Template'}
        </Typography.Title>
      }
      extra={
        <Space>
          <Button disabled={action != null} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            loading={action === 'draft'}
            disabled={action === 'publish'}
            onClick={onSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            type="primary"
            loading={action === 'publish'}
            disabled={action === 'draft'}
            onClick={onPublish}
          >
            Publish
          </Button>
        </Space>
      }
      styles={{ body: { padding: 16 } }}
    >
      {summary || fieldErrors.length || schemaErrors.length ? (
        <Alert
          type="error"
          message={summary || 'Save failed.'}
          description={
            fieldErrors.length || schemaErrors.length ? (
              <ul>
                {fieldErrors.map((e, i) => (
                  <li
                    key={`${e.Field}-${i}`}
                  >{`${e.Field}: ${e.Description}`}</li>
                ))}
                {schemaErrors.map((e, i) => (
                  <li key={`schema-${i}`}>{e}</li>
                ))}
              </ul>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item
          label="Template Key"
          name="templateKey"
          rules={
            isEdit
              ? []
              : [
                  {
                    required: true,
                    whitespace: true,
                    message: 'Template Key is required.',
                  },
                ]
          }
        >
          <Input aria-label="Template Key" disabled={isEdit} maxLength={200} />
        </Form.Item>
        <Form.Item
          label="Title"
          name="title"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Title is required.',
            },
          ]}
        >
          <Input aria-label="Title" maxLength={200} />
        </Form.Item>
        <Form.Item label="Scope">
          <Radio.Group
            value={scope}
            disabled={isEdit}
            onChange={(e) => setScope(e.target.value)}
          >
            <Radio value="global">Global</Radio>
            <Radio value="customer">Customer</Radio>
          </Radio.Group>
        </Form.Item>
        {scope === 'customer' ? (
          <Form.Item label="Customer">
            <Select
              value={customerId}
              disabled={isEdit}
              options={customers.map((c) => ({
                value: c.customerId,
                label: c.customerName,
              }))}
              onChange={setCustomerId}
            />
          </Form.Item>
        ) : null}
      </Form>
      <Tabs activeKey={tab} onChange={setTab} items={tabItems} />
    </Card>
  );
};
